/**
 * Shared Ryn Bridge models used by both web and NativeScript apps.
 */

export interface BridgeMessage {
  Id: string;
  From: string;
  To: string;
  Type: string;
  Subject: string;
  Body: string;
  Timestamp: string;
  Read: boolean;
}

export interface BridgeInstance {
  InstanceId: string;
  Role: string;
  Layer: string;
  LastSeen: string;
  Hostname: string | null;
  Groups: string[];
}

/**
 * Bridge registration endpoint path, appended to environment.urls.bridgeAPI.
 * Shared so the BridgeAuthInterceptor's anonymous bypass and every client's
 * register POST stay in lockstep, so a client that appends this to POST and the
 * interceptor that leaves this suffix unauthenticated read the SAME value.
 */
export const BRIDGE_REGISTER_PATH = '/register';

/**
 * Body of POST {bridgeAPI}/register, the one registration contract for every
 * Ryn client, web and NativeScript. A single union type by design: the Entra
 * path sends instanceId/role/layer/machineName/hostname plus an
 * `Authorization: Bearer <entra token>` header (no psk); the break-glass path
 * sets requestPsk to open the approval flow, then re-registers with psk. Only
 * instanceId is required; the bridge (RynBridge Register.cs) defaults role and
 * layer to general/channel when absent.
 */
export interface BridgeRegisterRequest {
  instanceId: string;
  role?: string;
  layer?: string;
  hostname?: string | null;
  machineName?: string | null;
  /** Pre-shared key. Break-glass path only; omit for the Entra Bearer path. */
  psk?: string;
  /** Ask the bridge to start the adaptive-card approval flow when no PSK is held. */
  requestPsk?: boolean;
  /** Isolation type, e.g. "isolated" for the call-ryn voice seat. */
  type?: string;
  /** One-shot/throwaway registration: expires on the short ephemeral window. */
  ephemeral?: boolean;
}

/**
 * Boot-time self-knowledge the register response carries (agent-registry row).
 * Null when the seat has no registry row or the identity columns are not
 * migrated yet (fail-open).
 */
export interface BridgeRegisterIdentity {
  defaultSendAs: string | null;
  defaultTimChatId: string | null;
  displayName: string | null;
  source: string;
}

/**
 * Success (200) body of POST {bridgeAPI}/register. `token` is present ONLY when
 * the caller authenticated (a valid PSK, or an Entra Bearer that passed the
 * Software Security feature check). A 200 WITHOUT a token is an anonymous
 * accept, so any client that needs auth MUST treat a token-less 200 as a
 * failure rather than a success.
 */
export interface BridgeRegisterResponse {
  registered: BridgeInstance;
  token?: string;
  identity?: BridgeRegisterIdentity | null;
}

/**
 * One row of the bridge's authoritative account store (GET /api/accounts):
 * an Entra identity the flock holds, with the seat its inbound routes to.
 * Metadata only, the endpoint never returns secret VALUES (passwordEnvVar is
 * the env-var NAME the bridge reads the password from).
 */
export interface RynAccount {
  upn: string;
  graphUserId: string | null;
  passwordEnvVar: string | null;
  routeToInstance: string | null;
  isMonitored: boolean | null;
  displayName: string | null;
  addedBy: string | null;
  addedAt: string | null;
}

export interface BridgeHealth {
  status: string;
  uptime: number;
  instances: number;
  messageCount: number;
  sseClients: number;
}

export interface BridgeSkill {
  name: string;
  displayName: string;
  description: string;
  triggerPhrases: string | null;
  targetRoles: string;
  slashCommand: string;
  lastSynced: string;
}

export interface BridgePerson {
  personId: number;
  displayName: string;
  email: string | null;
  department: string | null;
  title: string | null;
  company: number | null;
  office: string | null;
  timezone: string | null;
  communicationStyle: string | null;
  preferences: string | null;
  plannerPlanId: string | null;
  plannerGroupId: string | null;
  teamsChatId: string | null;
  discordChannelId: string | null;
  location: string | null;
  workingHoursStart: string | null;
  workingHoursEnd: string | null;
  roleType: string | null;
  organization: string | null;
  relationshipType: string | null;
  specialties: string | null;
  hrRef: number | null;
  graphUserId: string | null;
  pronouns: string | null;
  pronunciation: string | null;
  aiMaturityRating: number | null;
  firstRynInteractionDate: string | null;
  isActive: boolean;
  /** US 37759: when the active voice print was enrolled; null when not enrolled. Older bridges omit it. */
  voicePrintEnrolledAt?: string | null;
}

/** US 37759: one voice use and whether the person's signed consents allow it. */
export interface PersonVoiceUse {
  use: string;
  allowed: boolean;
  basis: string;
}

/** US 37759: one signed voice consent (or refusal) on file. */
export interface PersonVoiceConsent {
  id: number;
  kind: string;
  allowed: boolean;
  documentTitle: string;
  scope: string;
  signedOn: string;
  documentLink: string;
  revokedAt: string | null;
  recordedBy: string;
  revokedBy: string | null;
}

/** US 37759: the voice block on the single-person read. Never carries the print itself. */
export interface PersonVoice {
  enrolled: boolean;
  enrolledAt: string | null;
  consentAt: string | null;
  removedAt: string | null;
  printRef: string | null;
  uses: PersonVoiceUse[];
  consents: PersonVoiceConsent[];
  warnings: string[];
}

/** The single-person read (GET ryn-db/persons/{id}); voice is null until ryn-db is migrated. */
export interface BridgePersonDetail extends BridgePerson {
  voice?: PersonVoice | null;
}

export interface AiMaturityHistory {
  id: number;
  personId: number;
  rating: number;
  ratedBy: string | null;
  ratedAt: string;
  notes: string | null;
}

export interface AiMaturitySnapshot {
  personId: number;
  displayName: string;
  department: string | null;
  roleType: string | null;
  organization: string | null;
  rating: number;
  ratedAt: string;
  ratedBy: string | null;
  notes: string | null;
}

export interface TeamsChat {
  id: string;
  topic: string | null;
  chatType: string | null;
  lastMessage: string | null;
  lastSender: string | null;
  lastTime: string | null;
}

export interface ChatRouting {
  chatId: string;
  defaultInstances: string;
  displayName: string | null;
  members: string | null;
  setBy: string | null;
  lastUpdated: string | null;
}

export interface UserRouting {
  userId: string;
  defaultInstance: string;
  displayName: string | null;
  setBy: string | null;
  lastUpdated: string | null;
}

export interface PlannerPlan {
  id: string;
  title: string;
  owner: string | null;
  createdDateTime: string | null;
}

export interface PlannerTask {
  id: string;
  title: string;
  planId: string;
  bucketId: string | null;
  priority: number;
  percentComplete: number;
  dueDateTime: string | null;
  createdDateTime: string;
  assignees: string[];
  rynInstance: string | null;
  description?: string;
}

export interface PlannerTaskUpdate {
  title?: string;
  description?: string;
  priority?: number;
  percentComplete?: number;
  dueDateTime?: string | null;
  bucketId?: string | null;
}

export interface PlannerBucket {
  id: string;
  name: string;
  orderHint: string | null;
}

export function normalizeMessage(raw: Record<string, unknown>): BridgeMessage {
  return {
    Id: String(raw['Id'] ?? raw['id'] ?? ''),
    From: String(raw['From'] ?? raw['from'] ?? ''),
    To: String(raw['To'] ?? raw['to'] ?? ''),
    Type: String(raw['Type'] ?? raw['type'] ?? ''),
    Subject: String(raw['Subject'] ?? raw['subject'] ?? ''),
    Body: String(raw['Body'] ?? raw['body'] ?? ''),
    Timestamp: String(raw['Timestamp'] ?? raw['timestamp'] ?? ''),
    Read: Boolean(raw['Read'] ?? raw['read'] ?? false),
  };
}

export function normalizeRynAccount(raw: Record<string, unknown>): RynAccount {
  return {
    upn: String(raw['upn'] ?? raw['Upn'] ?? ''),
    graphUserId: (raw['graphUserId'] ?? raw['GraphUserId'] ?? null) as
      | string
      | null,
    passwordEnvVar: (raw['passwordEnvVar'] ?? raw['PasswordEnvVar'] ?? null) as
      | string
      | null,
    routeToInstance: (raw['routeToInstance'] ??
      raw['RouteToInstance'] ??
      null) as string | null,
    // Absent means unknown, deliberately NOT coerced to false so downstream
    // logic can stay fail-closed on its own terms.
    isMonitored: (raw['isMonitored'] ?? raw['IsMonitored'] ?? null) as
      | boolean
      | null,
    displayName: (raw['displayName'] ?? raw['DisplayName'] ?? null) as
      | string
      | null,
    addedBy: (raw['addedBy'] ?? raw['AddedBy'] ?? null) as string | null,
    addedAt: (raw['addedAt'] ?? raw['AddedAt'] ?? null) as string | null,
  };
}

export function normalizeInstance(
  raw: Record<string, unknown>
): BridgeInstance {
  return {
    InstanceId: (raw['InstanceId'] ?? raw['instanceId'] ?? '') as string,
    Role: (raw['Role'] ?? raw['role'] ?? '') as string,
    Layer: (raw['Layer'] ?? raw['layer'] ?? '') as string,
    LastSeen: (raw['LastSeen'] ?? raw['lastSeen'] ?? '') as string,
    Hostname: (raw['Hostname'] ?? raw['hostname'] ?? null) as string | null,
    Groups: (raw['Groups'] ?? raw['groups'] ?? []) as string[],
  };
}
